/**
 * Generate Study Dialog - Step 6: Select Sites
 *
 * Migrated from: lego/page/etmf/overview/generate-study/Step6.jsx
 *
 * Hierarchical multi-select table for selecting sites by country
 */

import React, { useEffect, useMemo, useState } from 'react'
import { FormikProps } from 'formik'
import { useDialogContext } from './context/useDialogContext'
import { useServiceContext } from '../../../../root'
import type { FormValues, SelectOption } from './context/DialogContext'
import Icon from '@mdsol/lego/lib/Icon'

interface Step6Props extends Partial<FormikProps<FormValues>> {}

export function Step6({ values, setFieldValue, errors }: Step6Props) {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const dialogData = useDialogContext()
  const [siteOptions, setSiteOptions] = useState<SelectOption[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>(values?.sites ?? [])
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Sort site options preserving country-sites grouping
  const sortedSiteOptions = useMemo(() => {
    // Group sites by their parent country
    const groups: { country: SelectOption; sites: SelectOption[] }[] = []
    let currentGroup: { country: SelectOption; sites: SelectOption[] } | null = null

    for (const option of siteOptions) {
      if (option.isCountry) {
        currentGroup = { country: option, sites: [] }
        groups.push(currentGroup)
      } else if (option.isSite && currentGroup) {
        currentGroup.sites.push(option)
      }
    }

    // Sort country groups by name
    groups.sort((a, b) => {
      const nameA = (a.country.label || '').toLowerCase()
      const nameB = (b.country.label || '').toLowerCase()
      if (nameA < nameB) return sortDir === 'asc' ? -1 : 1
      if (nameA > nameB) return sortDir === 'asc' ? 1 : -1
      return 0
    })

    // Sort sites within each group and flatten
    const result: SelectOption[] = []
    for (const group of groups) {
      result.push(group.country)
      const sortedSites = [...group.sites].sort((a, b) => {
        const nameA = (a.label || '').toLowerCase()
        const nameB = (b.label || '').toLowerCase()
        if (nameA < nameB) return sortDir === 'asc' ? -1 : 1
        if (nameA > nameB) return sortDir === 'asc' ? 1 : -1
        return 0
      })
      result.push(...sortedSites)
    }

    return result
  }, [siteOptions, sortDir])

  const handleSort = (e: React.MouseEvent) => {
    e.preventDefault()
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  useEffect(() => {
    loadSites()
  }, [])

  const loadSites = async () => {
    const result = await dialogData.loadSiteOptions({ sortDesc: true })
    setSiteOptions(result.elements)

    if (!result.isCached) {
      // Auto-select all countries AND sites that were selected in Step 5
      // Countries use 'country' prefix, sites use 'site' prefix
      const allCountryIds = result.elements.filter((opt) => opt.isCountry).map((opt) => 'country' + opt.id)
      const allSiteIds = result.elements.filter((opt) => opt.isSite).map((opt) => 'site' + opt.id)
      const allIds = [...allCountryIds, ...allSiteIds]
      setSelectedIds(allIds)
      setFieldValue?.('sites', allIds)
    } else {
      setSelectedIds(values?.sites ?? [])
    }
  }

  const handleSelectCountry = (country: SelectOption, checked: boolean) => {
    let newSelection = [...selectedIds]

    if (checked) {
      // Select country and all its sites - siteIds already have 'site' prefix from DialogState
      newSelection.push('country' + country.id)
      if (country.siteIds) {
        newSelection = Array.from(new Set([...newSelection, ...country.siteIds]))
      }
    } else {
      // Deselect country and all its sites
      newSelection = newSelection.filter((id) => id !== 'country' + country.id)
      if (country.siteIds) {
        newSelection = newSelection.filter((id) => !country.siteIds!.includes(id))
      }
    }

    setSelectedIds(newSelection)
    setFieldValue?.('sites', newSelection)
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all countries and sites
      const allCountryIds = siteOptions.filter((opt) => opt.isCountry).map((opt) => 'country' + opt.id)
      const allSiteIds = siteOptions.filter((opt) => opt.isSite).map((opt) => 'site' + opt.id)
      const allIds = [...allCountryIds, ...allSiteIds]
      setSelectedIds(allIds)
      setFieldValue?.('sites', allIds)
    } else {
      // Deselect all
      setSelectedIds([])
      setFieldValue?.('sites', [])
    }
  }

  const isAllSelected = () => {
    if (siteOptions.length === 0) return false
    const allCountryIds = siteOptions.filter((opt) => opt.isCountry).map((opt) => 'country' + opt.id)
    const allSiteIds = siteOptions.filter((opt) => opt.isSite).map((opt) => 'site' + opt.id)
    const allIds = [...allCountryIds, ...allSiteIds]
    return allIds.length > 0 && allIds.every((id) => selectedIds.includes(id))
  }

  const handleSelectSite = (site: SelectOption, checked: boolean) => {
    let newSelection = [...selectedIds]
    const siteId = 'site' + site.id

    if (checked) {
      // Add site with 'site' prefix
      newSelection.push(siteId)

      // Check if all sites for this country are now selected
      const country = siteOptions.find((opt) => opt.isCountry && site.countryId === 'country' + opt.id)
      if (country?.siteIds) {
        const allSitesSelected = country.siteIds.every((siteId) => newSelection.includes(siteId))
        if (allSitesSelected && !newSelection.includes('country' + country.id)) {
          newSelection.push('country' + country.id)
        }
      }
    } else {
      // Remove site and deselect country
      newSelection = newSelection.filter((id) => id !== siteId)
      if (site.countryId) {
        newSelection = newSelection.filter((id) => id !== site.countryId)
      }
    }

    setSelectedIds(newSelection)
    setFieldValue?.('sites', newSelection)
  }

  return (
    <div className="step step6">
      <div className="form-group w100p mb0">
        <div className="mcc-form-group">
          <label className="has-step-circle mb11px">
            <span className="step-circle">5</span>
            <span>{translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP6')}</span>
          </label>
        </div>
        {siteOptions.length === 0 ? (
          <div className="no-results">{translate('GENERATE_STUDY_NO_RESULTS_FILTERED_SITE_STEP')}</div>
        ) : (
          <div className="theme-sandman">
            <table className="chita-table fsiTable table">
              <thead>
                <tr>
                  <th className="clipped-cell with-checkbox bg-color-grey">
                    <input
                      type="checkbox"
                      name="selectAll"
                      checked={isAllSelected()}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th className="clipped-cell with-checkbox d-contents">{/* Site checkbox column (hidden) */}</th>
                  <th className="pl12 ta-left v-align-middle bg-color-grey" colSpan={2}>
                    <a href="#" className={`sort ed`} onClick={handleSort}>
                      <span className="fs14">{translate('NAME')}</span>
                      <span> </span>
                      <Icon className="icon-xsmall" name={sortDir === 'asc' ? 'carat-up' : 'carat-down'} />
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedSiteOptions.map((option) => (
                  <tr key={option.id}>
                    {option.isCountry && (
                      <>
                        <td className="clipped-cell with-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes('country' + option.id)}
                            onChange={(e) => handleSelectCountry(option, e.target.checked)}
                          />
                        </td>
                        <td className="d-contents" />
                        <td className="fs12-imp word-break" colSpan={2}>
                          <b>{option.label}</b>
                        </td>
                      </>
                    )}
                    {option.isSite && (
                      <>
                        <td className="clipped-cell with-checkbox" />
                        <td className="clipped-cell with-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes('site' + option.id)}
                            onChange={(e) => handleSelectSite(option, e.target.checked)}
                          />
                        </td>
                        <td className="pl0 ta-left v-align-middle fs12-imp word-break" title={option.label}>
                          {option.label}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {errors?.sites && <div className="error-message">{errors.sites}</div>}
      </div>
    </div>
  )
}

export default Step6
